import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { immunisationService } from '../services/immunisationService'
import { patientService } from '../services/patientService'
import { practiceVaccineService } from '../services/practiceVaccineService'
import { Immunisation } from '../types/immunisation'
import { PracticeVaccine } from '../types/practiceVaccine'
import { ImmunisationForm } from './ImmunisationForm'
import { VaccineSchedule } from './VaccineSchedule'
import { Modal } from './Modal'
import { generateVaccineSchedule } from '../utils/vaccineSchedule'
import './PatientImmunisations.css'

interface PatientImmunisationsProps {
  patientId: string
  readOnly?: boolean
}

type ViewMode = 'schedule' | 'history'

export function PatientImmunisations({ patientId, readOnly = false }: PatientImmunisationsProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState<ViewMode>('history')
  const [editingImmunisation, setEditingImmunisation] = useState<Immunisation | undefined>()
  const [showForm, setShowForm] = useState(false)
  const [selectedScheduledVaccine, setSelectedScheduledVaccine] = useState<string | null>(null)
  const canWrite = !readOnly

  // Fetch patient data for DOB
  const { data: patient } = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => patientService.getById(patientId),
    enabled: !!patientId,
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['patient-immunisations', patientId],
    queryFn: () => immunisationService.getPatientImmunisations(patientId),
  })

  // Get all practice vaccines to match against immunisations
  const { data: practiceVaccines } = useQuery({
    queryKey: ['practice-vaccines'],
    queryFn: () => practiceVaccineService.getPracticeVaccines(),
  })

  // Create a map of practice vaccine IDs to practice vaccine records
  const practiceVaccineMap = useMemo(() => {
    if (!practiceVaccines?.items) return new Map<string, PracticeVaccine>()
    const map = new Map<string, PracticeVaccine>()
    practiceVaccines.items.forEach((v) => {
      map.set(v.vaccineId, v)
    })
    return map
  }, [practiceVaccines])

  const createMutation = useMutation({
    mutationFn: (formData: Parameters<typeof immunisationService.createPatientImmunisation>[1]) =>
      immunisationService.createPatientImmunisation(patientId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-immunisations', patientId] })
      setShowForm(false)
      setEditingImmunisation(undefined)
      setSelectedScheduledVaccine(null)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({
      immunisationId,
      formData,
    }: {
      immunisationId: string
      formData: Parameters<typeof immunisationService.updatePatientImmunisation>[2]
    }) => immunisationService.updatePatientImmunisation(patientId, immunisationId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-immunisations', patientId] })
      setShowForm(false)
      setEditingImmunisation(undefined)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (immunisationId: string) =>
      immunisationService.deletePatientImmunisation(patientId, immunisationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-immunisations', patientId] })
    },
  })

  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    } catch {
      return dateString
    }
  }

  // Generate vaccine schedule if patient DOB is available
  const vaccineSchedule = useMemo(() => {
    if (!patient?.dateOfBirth) return null
    return generateVaccineSchedule(patient.dateOfBirth, patientId, data?.items || [])
  }, [patient?.dateOfBirth, patientId, data?.items])

  // Calculate schedule completion percentage
  const scheduleCompletion = useMemo(() => {
    if (!vaccineSchedule) return null
    const total = vaccineSchedule.scheduledVaccines.length
    const given = vaccineSchedule.scheduledVaccines.filter((sv) => sv.status === 'given').length
    return total > 0 ? Math.round((given / total) * 100) : 0
  }, [vaccineSchedule])

  // Count overdue vaccines
  const overdueCount = useMemo(() => {
    if (!vaccineSchedule) return 0
    return vaccineSchedule.scheduledVaccines.filter((sv) => sv.status === 'overdue').length
  }, [vaccineSchedule])

  const handleAdd = (scheduledVaccineId?: string) => {
    if (!canWrite) return
    setEditingImmunisation(undefined)
    setSelectedScheduledVaccine(scheduledVaccineId || null)
    setShowForm(true)
  }

  const handleEdit = (immunisation: Immunisation) => {
    if (!canWrite) return
    setEditingImmunisation(immunisation)
    setSelectedScheduledVaccine(null)
    setShowForm(true)
  }

  const handleDelete = async (immunisation: Immunisation) => {
    if (!canWrite) return
    if (!window.confirm(`Are you sure you want to delete this immunisation record?\n\nType: ${immunisation.type}\nDate: ${formatDate(immunisation.date)}`)) {
      return
    }

    try {
      await deleteMutation.mutateAsync(immunisation.immunisationId)
    } catch (err) {
      console.error('Failed to delete immunisation', err)
      window.alert('Failed to delete immunisation.')
    }
  }

  const handleSave = async (formData: Parameters<typeof immunisationService.createPatientImmunisation>[1]) => {
    if (editingImmunisation) {
      await updateMutation.mutateAsync({
        immunisationId: editingImmunisation.immunisationId,
        formData,
      })
    } else {
      await createMutation.mutateAsync(formData)
    }
  }

  if (isLoading) {
    return <div className="immunisations-loading">Loading immunisations...</div>
  }

  if (error) {
    return (
      <div className="immunisations-error" role="alert">
        Unable to load immunisations. Please try again.
      </div>
    )
  }

  const immunisations = data?.items || []
  const totalCount = data?.totalCount || 0

  // Find scheduled vaccine for form pre-fill
  const scheduledVaccineForForm = vaccineSchedule?.scheduledVaccines.find(
    (sv) => sv.scheduleId === selectedScheduledVaccine
  ) || null

  return (
    <div className="patient-immunisations" aria-label="Patient immunisations">
      <div className="immunisations-header-row">
        <div>
          <h2>Immunisations</h2>
          <p className="immunisations-subtitle">
            Record and track patient immunisations with full clinical details.
          </p>
        </div>
        <div className="immunisations-filters">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate(`/patients/${patientId}/immunisations/print`)}
          >
            Print history
          </button>
          <div className="view-mode-toggle">
            <button
              type="button"
              className={`view-mode-btn ${viewMode === 'schedule' ? 'active' : ''}`}
              onClick={() => setViewMode('schedule')}
              disabled={!patient?.dateOfBirth}
            >
              Schedule
            </button>
            <button
              type="button"
              className={`view-mode-btn ${viewMode === 'history' ? 'active' : ''}`}
              onClick={() => setViewMode('history')}
            >
              History
            </button>
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="immunisations-summary">
        <div className="summary-stat">
          <span className="stat-label">Total immunisations:</span>
          <span className="stat-value">{totalCount}</span>
        </div>
        {patient?.dateOfBirth && scheduleCompletion !== null && (
          <>
            <div className="summary-stat">
              <span className="stat-label">Schedule completion:</span>
              <span className="stat-value">{scheduleCompletion}%</span>
            </div>
            {overdueCount > 0 && (
              <div className="summary-stat warning">
                <span className="stat-label">Overdue vaccines:</span>
                <span className="stat-value">{overdueCount}</span>
              </div>
            )}
          </>
        )}
        {immunisations.length > 0 && (
          <div className="summary-stat">
            <span className="stat-label">Last immunisation:</span>
            <span className="stat-value">{formatDate(immunisations[0]?.date)}</span>
          </div>
        )}
      </div>

      {/* Schedule View */}
      {viewMode === 'schedule' && patient && (
        <div className="immunisations-schedule-view">
          <div className="schedule-header">
            <button
              type="button"
              className="btn-primary"
              onClick={() => handleAdd()}
              disabled={!canWrite}
              title={canWrite ? undefined : 'Secondary records are read-only'}
            >
              Add immunisation
            </button>
          </div>
          <VaccineSchedule
            patient={patient}
            immunisations={immunisations}
            onRecordImmunisation={(scheduledVaccineId) => handleAdd(scheduledVaccineId)}
          />
        </div>
      )}

      {/* History View */}
      {viewMode === 'history' && (
        <div className="immunisations-history-view">
          <div className="history-header">
            <h3>Immunisation History</h3>
            <button
              type="button"
              className="btn-primary"
              onClick={() => handleAdd()}
              disabled={!canWrite}
              title={canWrite ? undefined : 'Secondary records are read-only'}
            >
              Add immunisation
            </button>
          </div>

          {immunisations.length === 0 ? (
            <div className="immunisations-empty">No immunisations recorded.</div>
          ) : (
            <table className="immunisations-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Batch Number</th>
                  <th>Manufacturer</th>
                  <th>Expiry Date</th>
                  <th>Site</th>
                  <th>Method</th>
                  <th>Dose</th>
                  <th>Administered By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {immunisations.map((imm) => {
                  const practiceVaccine = imm.practiceVaccineId
                    ? practiceVaccineMap.get(imm.practiceVaccineId)
                    : null
                  const isFromPracticeInventory = !!practiceVaccine

                  return (
                    <tr key={imm.immunisationId}>
                      <td>{formatDate(imm.date)}</td>
                      <td>
                        <strong>{imm.type}</strong>
                      </td>
                      <td>
                        <div className="batch-number-cell">
                          {imm.batchNumber}
                          {isFromPracticeInventory && (
                            <span
                              className="practice-inventory-badge"
                              title={`From practice inventory: ${practiceVaccine?.location || 'N/A'} (Qty remaining: ${practiceVaccine?.quantity || 0})`}
                            >
                              📦
                            </span>
                          )}
                        </div>
                      </td>
                      <td>{imm.manufacturer}</td>
                      <td>
                        {formatDate(imm.expiryDate)}
                        {imm.expiredVaccineUsed && (
                          <span className="expired-badge" title="Expired vaccine was used">
                            ⚠️
                          </span>
                        )}
                      </td>
                      <td>{imm.siteGiven || '-'}</td>
                      <td>{imm.methodOfAdmin}</td>
                      <td>
                        {imm.dose.value} {imm.dose.unit}
                      </td>
                      <td>{imm.administeredByName}</td>
                      <td>
                        <div className="immunisations-actions">
                          {isFromPracticeInventory && (
                            <button
                              className="btn-link small"
                              onClick={() => {
                                // Navigate to practice vaccine settings
                                window.open(`/settings/practice-vaccines`, '_blank')
                              }}
                              title={`View practice vaccine: ${practiceVaccine?.batchNumber} (Location: ${practiceVaccine?.location || 'N/A'}, Remaining: ${practiceVaccine?.quantity || 0})`}
                            >
                              📦
                            </button>
                          )}
                          <button
                            className="btn-link small"
                            onClick={() => handleEdit(imm)}
                            disabled={updateMutation.isPending || !canWrite}
                            title={canWrite ? 'Edit immunisation' : 'Secondary records are read-only'}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-link small danger"
                            onClick={() => handleDelete(imm)}
                            disabled={deleteMutation.isPending || !canWrite}
                            title={canWrite ? 'Delete immunisation' : 'Secondary records are read-only'}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Form Modal */}
      {canWrite && showForm && (
        <Modal
          isOpen={showForm}
          onClose={() => {
            setShowForm(false)
            setEditingImmunisation(undefined)
            setSelectedScheduledVaccine(null)
          }}
          title={editingImmunisation ? 'Edit Immunisation' : 'Add Immunisation'}
          size="large"
        >
          <ImmunisationForm
            patientId={patientId}
            initialImmunisation={editingImmunisation}
            scheduledVaccine={scheduledVaccineForForm}
            onSubmit={handleSave}
            onCancel={() => {
              setShowForm(false)
              setEditingImmunisation(undefined)
              setSelectedScheduledVaccine(null)
            }}
          />
        </Modal>
      )}
    </div>
  )
}
